import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronDown } from 'lucide-react';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { apiClientsApi } from '../../../api/apiClients.js';
import { permissionsApi } from '../../../api/permissions.js';
import { ApiError } from '../../../api/client.js';
import { t } from '../../../lib/i18n.js';
import { friendlyScope, groupLabel, groupOrder, INTEGRATION_GROUPS } from './scopeLabels.js';

const EMPTY = {
  name: '',
  description: '',
  scopes: [],
  allowedGrantTypes: ['client_credentials', 'refresh_token'],
  allowedIps: [],
  allowedAudiences: [],
  tokenTtlSec: '',
  refreshTtlSec: '',
  rateLimitTier: 'default',
  status: 'active'
};

function ScopePicker({ permissions, value, onChange }) {
  const [showAll, setShowAll] = useState(false);

  const groups = useMemo(() => {
    const byGroup = new Map();
    for (const p of permissions) {
      const g = p.group || 'altro';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(p);
    }
    return [...byGroup.entries()]
      .map(([g, perms]) => ({ group: g, perms }))
      .sort((a, b) => groupOrder(a.group) - groupOrder(b.group));
  }, [permissions]);

  const visible = showAll ? groups : groups.filter((g) => INTEGRATION_GROUPS.includes(g.group));
  const selected = new Set(value);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange([...next]);
  };

  return (
    <div className="flex flex-col gap-3">
      {visible.map(({ group, perms }) => (
        <div key={group}>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">{groupLabel(group)}</p>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {perms.map((p) => (
              <label key={p.key} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-surface-subtle">
                <input type="checkbox" className="h-4 w-4 accent-ink" checked={selected.has(p.key)} onChange={() => toggle(p.key)} />
                <span className="text-ink">{friendlyScope(p.key)}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => setShowAll((s) => !s)} className="self-start text-xs text-brand hover:text-brand-hover">
        {showAll ? 'Mostra solo i permessi per integrazioni' : 'Mostra tutti i permessi (avanzato)'}
      </button>
    </div>
  );
}

export function ClientFormModal({ open, onClose, initial, onCreated }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const permsQuery = useQuery({ queryKey: ['permissions', 'all'], queryFn: () => permissionsApi.list(), enabled: open });
  const permissions = useMemo(() => (permsQuery.data && permsQuery.data.items) || [], [permsQuery.data]);

  useEffect(() => {
    if (!open) return;
    setShowAdvanced(false);
    setForm(initial ? {
      ...EMPTY,
      ...initial,
      description: initial.description || '',
      tokenTtlSec: initial.tokenTtlSec ?? '',
      refreshTtlSec: initial.refreshTtlSec ?? ''
    } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const grantOn = (g) => form.allowedGrantTypes.includes(g);
  const toggleGrant = (g) => set('allowedGrantTypes', grantOn(g) ? form.allowedGrantTypes.filter((x) => x !== g) : [...form.allowedGrantTypes, g]);

  const mutation = useMutation({
    mutationFn: async () => {
      const num = (v) => (v === '' || v === null ? undefined : Number(v));
      if (editing) {
        return apiClientsApi.update(initial._id, {
          name: form.name,
          description: form.description || null,
          scopes: form.scopes,
          allowedGrantTypes: form.allowedGrantTypes,
          allowedIps: form.allowedIps,
          allowedAudiences: form.allowedAudiences,
          tokenTtlSec: num(form.tokenTtlSec) ?? null,
          refreshTtlSec: num(form.refreshTtlSec) ?? null,
          rateLimitTier: form.rateLimitTier,
          status: form.status
        });
      }
      const body = {
        name: form.name,
        description: form.description || undefined,
        scopes: form.scopes,
        allowedGrantTypes: form.allowedGrantTypes,
        allowedIps: form.allowedIps,
        allowedAudiences: form.allowedAudiences,
        rateLimitTier: form.rateLimitTier
      };
      const tt = num(form.tokenTtlSec); if (tt) body.tokenTtlSec = tt;
      const rt = num(form.refreshTtlSec); if (rt) body.refreshTtlSec = rt;
      return apiClientsApi.create(body);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-clients'] });
      if (!editing && data && data.clientSecret) onCreated && onCreated({ client: data.client, clientSecret: data.clientSecret });
      else toast.success('Client aggiornato.');
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const submit = () => {
    if (!form.name.trim()) return toast.error('Nome richiesto.');
    if (!form.scopes.length) return toast.error('Seleziona almeno un permesso (cosa può fare).');
    if (!form.allowedGrantTypes.length) return toast.error('Seleziona almeno una modalita di accesso.');
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? 'Modifica client API' : 'Nuovo client API'}
      description="Da' a un sistema esterno (gestionale, e-commerce, cassa) l'accesso alle API."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button onClick={submit} loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Nome" placeholder="es. Cassa negozio, Shopify..." value={form.name} onChange={(e) => set('name', e.target.value)} />
          <Input label="Descrizione" placeholder="A cosa serve questo accesso" value={form.description} onChange={(e) => set('description', e.target.value)} />
        </div>

        <div>
          <p className="text-sm font-medium text-ink">Cosa puo fare</p>
          <p className="mb-2 text-xs text-ink-muted">Scegli le azioni consentite al sistema esterno. Per un gestionale o una cassa di solito bastano i permessi su Prodotti e Negozi.</p>
          {permsQuery.isLoading
            ? <p className="text-xs text-ink-muted">Caricamento permessi...</p>
            : <ScopePicker permissions={permissions} value={form.scopes} onChange={(v) => set('scopes', v)} />}
        </div>

        {editing && (
          <Select label="Stato" value={form.status} onChange={(e) => set('status', e.target.value)} className="max-w-xs">
            <option value="active">Attivo</option>
            <option value="revoked">Revocato (accesso bloccato)</option>
          </Select>
        )}

        {/* Advanced */}
        <div className="rounded-lg border border-border">
          <button type="button" onClick={() => setShowAdvanced((s) => !s)} className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-medium text-ink">
            Impostazioni avanzate (facoltative)
            <ChevronDown className={`h-4 w-4 text-ink-faint transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>
          {showAdvanced && (
            <div className="flex flex-col gap-4 border-t border-border p-4">
              <div>
                <p className="text-sm font-medium text-ink">Modalita di accesso</p>
                <p className="mb-2 text-xs text-ink-muted">Lascia entrambe attive se non sai cosa scegliere.</p>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                    <span>Accesso diretto (client credentials)</span>
                    <Switch checked={grantOn('client_credentials')} onChange={() => toggleGrant('client_credentials')} />
                  </label>
                  <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                    <span>Rinnovo automatico del token (refresh)</span>
                    <Switch checked={grantOn('refresh_token')} onChange={() => toggleGrant('refresh_token')} />
                  </label>
                </div>
              </div>

              <div>
                <TagInput label="IP consentiti" value={form.allowedIps} onChange={(v) => set('allowedIps', v)} hint="Se compili, l'accesso funziona solo da questi indirizzi IP. Vuoto = ovunque." />
              </div>
              <div>
                <TagInput label="Destinatari (audience)" value={form.allowedAudiences} onChange={(v) => set('allowedAudiences', v)} hint="Avanzato: lascia vuoto. Limita il token a specifiche API interne. Quasi mai necessario." />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input label="Durata token (s)" type="number" min="60" max="86400" placeholder="default" value={form.tokenTtlSec} onChange={(e) => set('tokenTtlSec', e.target.value)} hint="Vuoto = default" />
                <Input label="Durata rinnovo (s)" type="number" min="300" placeholder="default" value={form.refreshTtlSec} onChange={(e) => set('refreshTtlSec', e.target.value)} hint="Vuoto = default" />
                <Select label="Limite richieste" value={form.rateLimitTier} onChange={(e) => set('rateLimitTier', e.target.value)}>
                  <option value="default">Standard</option>
                  <option value="high">Alto volume</option>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
